import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import CampaignScopeToolbar from '../../components/crm/CampaignScopeToolbar';
import MetricsCard from './components/MetricsCard';
import CallActivityChart from './components/CallActivityChart';
import CallRecordsTable from './components/CallRecordsTable';
import ProspectDetailModal from '../../components/ProspectDetailModal';
import { fetchCalls } from '../../api/calls';
import { fetchProspects, apiFetch } from '../../api/prospects';
import { fetchCampaigns } from '../../api/campaigns';
import { filterCallsByProspectIds } from '../../utils/pipelineStats';

const PERIOD_OPTIONS = [
  { value: '7', label: '7 derniers jours' },
  { value: '30', label: '30 derniers jours' },
  { value: '90', label: '90 derniers jours' },
];

function computeFromCalls(calls) {
  const consideredStatuses = ['success', 'callback', 'not-interested'];
  const consideredCalls = calls.filter((c) => consideredStatuses.includes(c.status));

  const totalCalls = calls.length;
  const totalDuration = calls.reduce((acc, c) => acc + (c.timeDuration || 0), 0);
  const avgDurationSec = totalCalls ? totalDuration / totalCalls : 0;

  const successCalls = consideredCalls.filter((c) => c.status === 'success').length;
  const callbackCalls = consideredCalls.filter((c) => c.status === 'callback').length;

  const baseCount = consideredCalls.length;
  const successRate = baseCount ? (successCalls / baseCount) * 100 : 0;
  const callbackRate = baseCount ? (callbackCalls / baseCount) * 100 : 0;

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const metrics = [
    {
      title: "Volume d'appels",
      value: totalCalls.toString(),
      unit: 'appels',
      icon: 'Phone',
      iconColor: 'var(--color-primary)',
    },
    {
      title: 'Durée moyenne',
      value: formatDuration(avgDurationSec),
      unit: 'min',
      icon: 'Clock',
      iconColor: 'var(--color-secondary)',
    },
    {
      title: 'Taux de succès',
      value: successRate.toFixed(1),
      unit: '%',
      icon: 'CheckCircle',
      iconColor: 'var(--color-success)',
    },
    {
      title: 'Suivi terminé',
      value: successCalls.toString(),
      unit: 'appels',
      icon: 'UserCheck',
      iconColor: 'var(--color-accent)',
    },
    {
      title: 'Taux de rappel',
      value: callbackRate.toFixed(1),
      unit: '%',
      icon: 'PhoneForwarded',
      iconColor: 'var(--color-warning)',
    },
  ];

  const buckets = {};
  calls.forEach((c) => {
    const d = new Date(c.startCallAt);
    const sortKey = d.toISOString().slice(0, 10);
    const dayKey = d.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
    });
    if (!buckets[sortKey]) {
      buckets[sortKey] = {
        time: dayKey,
        sortKey,
        total: 0,
        success: 0,
        failed: 0,
        callback: 0,
      };
    }
    buckets[sortKey].total += 1;
    if (c.status === 'success') {
      buckets[sortKey].success += 1;
    } else if (c.status === 'callback') {
      buckets[sortKey].callback += 1;
      buckets[sortKey].success += 1;
    } else {
      buckets[sortKey].failed += 1;
    }
  });

  const chart = Object.values(buckets).sort((a, b) =>
    String(a.sortKey).localeCompare(String(b.sortKey)),
  );

  const records = calls.map((c) => ({
    id: c._id,
    prospectId: c.prospect?._id || c.prospect,
    contactName: c.prospect?.fullName || 'Prospect',
    contactPhone: c.prospect?.phone || '',
    timestamp: c.startCallAt,
    duration: c.timeDuration || 0,
    outcome: c.status,
    agentName: c.user?.email || 'Agent',
  }));

  return { metrics, chart, records };
}

const CallPerformanceAnalytics = () => {
  const [metricsData, setMetricsData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [callRecords, setCallRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProspectId, setSelectedProspectId] = useState(null);
  const [campaignId, setCampaignId] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [period, setPeriod] = useState('7');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingCampaigns(true);
      try {
        const res = await fetchCampaigns();
        if (!cancelled) setCampaigns(res?.data?.campaigns || []);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoadingCampaigns(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const now = new Date();
      const from = new Date();
      from.setDate(from.getDate() - Number(period || 7));
      from.setHours(0, 0, 0, 0);

      const meRes = await apiFetch('/me');
      const me = meRes?.data?.user;
      const isAdmin = me?.permission === 'admin';

      let prospectIdSet = null;
      if (campaignId) {
        const pRes = await fetchProspects({
          campaignId,
          limit: 200,
          ...(!isAdmin && me?._id ? { assignedTo: me._id } : {}),
        });
        const ids = (pRes?.data?.prospects || []).map((p) => String(p._id));
        prospectIdSet = new Set(ids);
      }

      const callsRes = await fetchCalls({
        ...(isAdmin ? {} : { userId: me._id }),
        from,
        to: now,
      });
      let calls = callsRes?.data?.calls || [];
      if (prospectIdSet) {
        calls = filterCallsByProspectIds(calls, prospectIdSet);
      }

      const { metrics, chart, records } = computeFromCalls(calls);
      setMetricsData(metrics);
      setChartData(chart);
      setCallRecords(records);
    } catch (e) {
      console.error(e);
      setError("Impossible de charger les statistiques d'appels.");
    } finally {
      setLoading(false);
    }
  }, [campaignId, period]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const campaignName = useMemo(() => {
    if (!campaignId) return null;
    const c = campaigns.find((x) => String(x._id || x.id) === String(campaignId));
    return c?.name || null;
  }, [campaignId, campaigns]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Breadcrumb />
      <main className="main-content mx-auto max-w-7xl">
        <div className="mb-6 md:mb-8">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">Activité commerciale</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
            Performance
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            {campaignName
              ? `Campagne « ${campaignName} » — volume, durée et résultats sur la période.`
              : 'Vue globale de vos appels : volume, durée et taux de succès.'}
          </p>
        </div>

        <CampaignScopeToolbar
          className="mb-8"
          campaigns={campaigns}
          campaignId={campaignId}
          onCampaignChange={setCampaignId}
          loadingCampaigns={loadingCampaigns}
          period={period}
          onPeriodChange={setPeriod}
          periodOptions={PERIOD_OPTIONS}
        />

        {error ? (
          <p className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {error}
          </p>
        ) : null}

        {loading ? (
          <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="h-32 animate-pulse rounded-xl border border-border bg-muted/40"
              />
            ))}
          </div>
        ) : (
          <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {metricsData.map((metric, index) => (
              <MetricsCard key={index} {...metric} />
            ))}
          </div>
        )}

        <section className="mb-8 space-y-6">
          {loading ? (
            <div className="h-72 animate-pulse rounded-2xl border border-border bg-muted/30" />
          ) : (
            <CallActivityChart data={chartData} />
          )}

          {!loading && callRecords.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-border bg-muted/15 px-4 py-10 text-center text-sm text-muted-foreground">
              Aucun appel sur cette période pour le périmètre sélectionné.
            </p>
          ) : null}

          {!loading && callRecords.length > 0 ? (
        <CallRecordsTable
          records={callRecords}
          onContactClick={(record) =>
            record?.prospectId && setSelectedProspectId(record.prospectId)
          }
        />
          ) : null}
        </section>
      </main>

      {selectedProspectId ? (
        <ProspectDetailModal
          prospectId={selectedProspectId}
          onClose={() => setSelectedProspectId(null)}
        />
      ) : null}
    </div>
  );
};

export default CallPerformanceAnalytics;
